import { useEffect, useMemo, useRef, useState, type PointerEvent as ReactPointerEvent } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { isSameDay } from 'date-fns';
import { apiClient } from '@/services/api';
import type {
  BookingMutationPayload,
  BookingScheduleInfo,
  BookingSectionInfo,
  SpecialDayInfo,
  SyncedBookingInfo,
  SyncedBookingStatus,
  SyncedTableInfo,
} from '@/types/api';
import type { BookingPrefill } from './BookingFormModal';
import TimelineBar, { type Bar } from './TimelineBar';
import TimelineGrid from './TimelineGrid';
import {
  DEFAULT_DURATION_MINUTES,
  HEADER_HEIGHT,
  HOUR_WIDTH,
  MIN_DURATION_MINUTES,
  ROW_HEIGHT,
  SNAP_MINUTES,
  TABLE_LABEL_WIDTH,
  bookingSpan,
  clampMinutes,
  csvToIds,
  groupContiguous,
  instantsOverlap,
  minutesToX,
  rangeWidthPx,
  toDisplayMinutes,
  xToMinutes,
  yToTableIndex,
  type HourRange,
} from '@/utils/timeline';

interface BookingsTimelineViewProps {
  bookings: SyncedBookingInfo[];
  tables: SyncedTableInfo[];
  sections: BookingSectionInfo[];
  schedules: BookingScheduleInfo[];
  specialDays: SpecialDayInfo[];
  isLoading: boolean;
  restaurantId: number;
  selectedDate: string; // yyyy-MM-dd
  /** Hours the business day extends past midnight (restaurant config). */
  extendedDayHours?: number;
  canUpdate: boolean;
  canCreate: boolean;
  selectedBookingUuid: string | null;
  onSelectBooking: (uuid: string | null) => void;
  onEditBooking?: (booking: SyncedBookingInfo) => void;
  onCreateBooking?: (prefill: BookingPrefill) => void;
}

type DragMode = 'move' | 'start' | 'end' | 'top' | 'bottom';

interface DragState {
  key: string;
  booking: SyncedBookingInfo;
  mode: DragMode;
  // live geometry
  startMin: number;
  endMin: number;
  tableIndices: number[];
  // grab reference
  grabOffsetMin: number;
  grabOffsetRow: number;
  origStartMin: number;
  origEndMin: number;
  origIndices: number[];
}

interface Override {
  key: string;
  startMin: number;
  endMin: number;
  tableIndices: number[];
}

const tableName = (t: SyncedTableInfo): string =>
  t.tableName && t.tableName.trim() ? t.tableName : `Table ${t.posTableId}`;

const byPosId = (a: SyncedTableInfo, b: SyncedTableInfo) => (a.posTableId ?? 0) - (b.posTableId ?? 0);

const NAMED_COLORS: Record<string, string> = {
  red: '#dc3545',
  green: '#28a745',
  blue: '#007bff',
  yellow: '#ffc107',
  orange: '#fd7e14',
  purple: '#6f42c1',
  pink: '#e83e8c',
  cyan: '#17a2b8',
  gray: '#6c757d',
};

const parseSectionColor = (raw: string | null | undefined): string | null => {
  if (!raw) return null;
  const key = raw.toLowerCase();
  if (NAMED_COLORS[key]) return NAMED_COLORS[key];
  const hex = raw.startsWith('#') ? raw : `#${raw}`;
  if (/^#[0-9a-fA-F]{6}$/.test(hex)) return hex;
  return null;
};

/** minutes-of-day on the selected date -> ISO instant in local time. */
const minutesOfDayToIso = (minutesOfDay: number, dateStr: string): string => {
  const [y, mo, da] = dateStr.split('-').map(Number);
  const d = new Date(y, mo - 1, da, Math.floor(minutesOfDay / 60), minutesOfDay % 60, 0, 0);
  return d.toISOString();
};

const errMessage = (e: unknown): string => {
  const r = e as { response?: { data?: { message?: string } } };
  return r.response?.data?.message ?? 'Failed to update booking';
};

export default function BookingsTimelineView({
  bookings,
  tables,
  sections,
  schedules,
  specialDays,
  isLoading,
  restaurantId,
  selectedDate,
  extendedDayHours = 0,
  canUpdate,
  canCreate,
  selectedBookingUuid,
  onEditBooking,
  onCreateBooking,
}: BookingsTimelineViewProps) {
  const queryClient = useQueryClient();
  const canvasRef = useRef<HTMLDivElement>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const centeredForDateRef = useRef<string | null>(null);
  const movedRef = useRef(false);
  const suppressClickRef = useRef(false);
  const activeRef = useRef(false); // true while a drag or commit is in flight

  const [drag, setDrag] = useState<DragState | null>(null);
  const [pending, setPending] = useState<Override | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  // ---- Floors ---------------------------------------------------------------
  const floors = useMemo(() => {
    const m = new Map<number, string>();
    for (const t of tables) {
      if (t.floorId != null) m.set(t.floorId, t.floorName ?? `Floor ${t.floorId}`);
    }
    return [...m.entries()]
      .map(([id, name]) => ({ id, name }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [tables]);

  const [floorId, setFloorId] = useState<number | null>(null);
  const effectiveFloorId = floorId != null && floors.some((f) => f.id === floorId)
    ? floorId
    : floors[0]?.id ?? null;

  const visibleTables = useMemo(
    () =>
      tables
        .filter((t) => effectiveFloorId == null || t.floorId === effectiveFloorId)
        .sort(byPosId),
    [tables, effectiveFloorId],
  );

  // ---- Hour range -----------------------------------------------------------
  // Always a full 24h business day starting at the restaurant's extended-day
  // hour (e.g. ext=4 ⇒ 04:00 → 04:00 next day), matching the POS timeline.
  const startHour = Math.min(23, Math.max(0, Math.trunc(extendedDayHours)));
  const range = useMemo<HourRange>(
    () => ({ startHour, endHour: startHour + 24 }),
    [startHour],
  );

  // ---- Closed day -----------------------------------------------------------
  // A day is closed for bookings when a closed SpecialDay matches, or the
  // weekday has an enabled schedule with no sections (no-section = closed).
  const dayClosed = useMemo(() => {
    const [y, mo, da] = selectedDate.split('-').map(Number);
    const js = new Date(y, mo - 1, da).getDay(); // 0=Sun..6=Sat
    const weekday = js === 0 ? 7 : js; // 1=Mon..7=Sun
    const sd = specialDays.find((d) => d.date?.slice(0, 10) === selectedDate);
    if (sd?.isClosed) return true;
    return schedules
      .filter((s) => s.enabled && s.dayOfWeek === weekday)
      .some((s) => !s.sectionIds || s.sectionIds.length === 0);
  }, [schedules, specialDays, selectedDate]);

  // ---- Lookups --------------------------------------------------------------
  const nameByPosId = useMemo(() => {
    const m = new Map<number, string>();
    for (const t of tables) m.set(t.posTableId, tableName(t));
    return m;
  }, [tables]);

  const bookingsByPosId = useMemo(() => {
    const m = new Map<number, SyncedBookingInfo[]>();
    for (const b of bookings) {
      for (const id of csvToIds(b.shopTableIds)) {
        const list = m.get(id) ?? [];
        list.push(b);
        m.set(id, list);
      }
    }
    return m;
  }, [bookings]);

  // ---- Section colour blocks (header) --------------------------------------
  // Lay each day-schedule's sections back-to-back FROM THE SCHEDULE OPEN TIME
  // (extended-day aware), mirroring the POS computeSectionBlocks(). Each band
  // carries its start/end minutes and section name for click resolution.
  const weekday = useMemo(() => {
    const [y, mo, da] = selectedDate.split('-').map(Number);
    const js = new Date(y, mo - 1, da).getDay(); // 0=Sun..6=Sat
    return js === 0 ? 7 : js; // 1=Mon..7=Sun
  }, [selectedDate]);

  const sectionById = useMemo(() => {
    const m = new Map<number, BookingSectionInfo>();
    for (const s of sections) m.set(s.id, s);
    return m;
  }, [sections]);

  const sectionBlocks = useMemo(() => {
    const blocks: {
      startMin: number; endMin: number; x: number; width: number; color: string | null; name: string;
    }[] = [];
    const displayStart = range.startHour * 60;
    const displayEnd = displayStart + 24 * 60;

    const parseHHmm = (t: string | null | undefined): number | null => {
      if (!t) return null;
      const [h, mm] = t.split(':').map(Number);
      if (!Number.isFinite(h)) return null;
      return h * 60 + (Number.isFinite(mm) ? mm : 0);
    };

    for (const sch of schedules.filter((s) => s.enabled && s.dayOfWeek === weekday)) {
      const openMin = parseHHmm(sch.openTime);
      const ids = sch.sectionIds ?? [];
      if (openMin == null || ids.length === 0) continue;

      const secs = ids
        .map((id) => sectionById.get(id))
        .filter((s): s is BookingSectionInfo => !!s && s.enabled !== false)
        .sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0));

      let cursor = toDisplayMinutes(openMin, range);
      for (const s of secs) {
        const dur = s.defaultDurationMinutes || DEFAULT_DURATION_MINUTES;
        const start = cursor;
        const end = Math.min(cursor + dur, displayEnd);
        if (end > start) {
          const x = minutesToX(start, range);
          blocks.push({ startMin: start, endMin: end, x, width: minutesToX(end, range) - x, color: parseSectionColor(s.color), name: s.name });
        }
        cursor = end;
        if (cursor >= displayEnd) break;
      }
    }
    return blocks;
  }, [schedules, weekday, sectionById, range]);

  // Leftmost section band x (grid-relative px) for the selected day, or null
  // when the day has no sections. Used to scroll non-today dates to the start
  // of the first section instead of 00:00.
  const firstSectionX = useMemo(() => {
    if (!sectionBlocks.length) return null;
    return sectionBlocks.reduce((min, b) => Math.min(min, b.x), Infinity);
  }, [sectionBlocks]);

  // The section name whose band contains the given display-minute, or null.
  const sectionAt = (displayMin: number): string | null => {
    for (const b of sectionBlocks) {
      if (displayMin >= b.startMin && displayMin < b.endMin) return b.name;
    }
    return null;
  };

  // ---- Bars (with live override) -------------------------------------------
  const override: Override | null = drag
    ? { key: drag.key, startMin: drag.startMin, endMin: drag.endMin, tableIndices: drag.tableIndices }
    : pending;

  const bars: Bar[] = useMemo(() => {
    if (!range) return [];
    const indexByPos = new Map<number, number>();
    visibleTables.forEach((t, i) => indexByPos.set(t.posTableId, i));

    const out: Bar[] = [];
    for (const b of bookings) {
      const span = bookingSpan(b.startTime, b.endTime, range);
      if (!span) continue;
      const idxs = csvToIds(b.shopTableIds)
        .map((id) => indexByPos.get(id))
        .filter((n): n is number => n != null);
      if (idxs.length === 0) continue;
      for (const group of groupContiguous(idxs)) {
        out.push({
          key: `${b.uuid ?? b.id}:${group[0]}`,
          booking: b,
          tableIndices: group,
          x: span.x,
          width: span.width,
          draggable: !!b.uuid && canUpdate,
        });
      }
    }
    return out;
  }, [bookings, visibleTables, range, canUpdate]);

  // Apply the live override geometry to the matching bar.
  const renderedBars: Bar[] = useMemo(() => {
    if (!override || !range) return bars;
    return bars.map((bar) => {
      if (bar.key !== override.key) return bar;
      const x = minutesToX(override.startMin, range);
      const width = minutesToX(override.endMin, range) - x;
      return { ...bar, x, width, tableIndices: override.tableIndices };
    });
  }, [bars, override, range]);

  // ---- Now line -------------------------------------------------------------
  const isToday = useMemo(() => {
    const [y, mo, da] = selectedDate.split('-').map(Number);
    return isSameDay(new Date(), new Date(y, mo - 1, da));
  }, [selectedDate]);

  const nowX = useMemo(() => {
    if (!isToday || !range) return null;
    const now = new Date();
    const min = toDisplayMinutes(now.getHours() * 60 + now.getMinutes(), range);
    if (min < range.startHour * 60 || min > range.endHour * 60) return null;
    return minutesToX(min, range);
  }, [isToday, range]);

  // Position the horizontal scroll when the timeline opens (once per selected
  // date): today centers on the "now" line; other days align to the start of
  // the first section. Falls through without marking the date done until the
  // data needed to compute a target has loaded.
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    if (centeredForDateRef.current === selectedDate) return;

    let target: number | null = null;
    if (nowX != null) {
      // Today with a visible now line → center it.
      target = TABLE_LABEL_WIDTH + nowX - el.clientWidth / 2;
    } else if (firstSectionX != null) {
      // Other days → bring the first section to the left edge, with a little
      // breathing room before it.
      target = firstSectionX - HOUR_WIDTH * 0.25;
    }
    if (target == null) return;

    el.scrollLeft = Math.max(0, target);
    centeredForDateRef.current = selectedDate;
  }, [selectedDate, nowX, firstSectionX]);

  // ---- Mutation -------------------------------------------------------------
  const mutation = useMutation({
    mutationFn: ({ uuid, payload }: { uuid: string; payload: BookingMutationPayload }) =>
      apiClient.updateSyncedBooking(restaurantId, uuid, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['synced-bookings', restaurantId] });
    },
    onError: (e: unknown) => setNotice(errMessage(e)),
    onSettled: () => {
      setPending(null);
      activeRef.current = false;
    },
  });

  // ---- Auto-refresh (today only, paused while interacting) ------------------
  useEffect(() => {
    if (!isToday) return;
    const id = window.setInterval(() => {
      if (!activeRef.current) {
        queryClient.invalidateQueries({ queryKey: ['synced-bookings', restaurantId] });
      }
    }, 60_000);
    return () => window.clearInterval(id);
  }, [isToday, restaurantId, queryClient]);

  // ---- Notice auto-clear ----------------------------------------------------
  useEffect(() => {
    if (!notice) return;
    const id = window.setTimeout(() => setNotice(null), 4000);
    return () => window.clearTimeout(id);
  }, [notice]);

  // ---- Pointer geometry helper ---------------------------------------------
  const pointerToCanvas = (e: ReactPointerEvent): { px: number; py: number } | null => {
    const el = canvasRef.current;
    if (!el) return null;
    const rect = el.getBoundingClientRect();
    return { px: e.clientX - rect.left, py: e.clientY - rect.top };
  };

  // ---- Drag handlers --------------------------------------------------------
  const handleBarPointerDown = (e: ReactPointerEvent, bar: Bar) => {
    if (!bar.draggable || !range) return;
    e.stopPropagation();
    const pt = pointerToCanvas(e);
    if (!pt) return;
    const handle = (e.target as HTMLElement).dataset.handle as DragMode | undefined;
    const mode: DragMode = handle ?? 'move';

    const pointerMin = xToMinutes(pt.px, range);
    const pointerRow = yToTableIndex(pt.py);
    const span = bookingSpan(bar.booking.startTime, bar.booking.endTime, range);
    if (!span) return;
    const topIndex = bar.tableIndices[0];

    movedRef.current = false;
    activeRef.current = true;
    (e.target as HTMLElement).setPointerCapture?.(e.pointerId);
    setDrag({
      key: bar.key,
      booking: bar.booking,
      mode,
      startMin: span.startMin,
      endMin: span.endMin,
      tableIndices: [...bar.tableIndices],
      grabOffsetMin: pointerMin - span.startMin,
      grabOffsetRow: pointerRow - topIndex,
      origStartMin: span.startMin,
      origEndMin: span.endMin,
      origIndices: [...bar.tableIndices],
    });
  };

  const snap = (m: number) => Math.round(m / SNAP_MINUTES) * SNAP_MINUTES;

  const handleBarPointerMove = (e: ReactPointerEvent) => {
    if (!drag || !range) return;
    const pt = pointerToCanvas(e);
    if (!pt) return;
    movedRef.current = true;

    const pointerMin = xToMinutes(pt.px, range);
    const rowCount = visibleTables.length;
    const pointerRow = Math.max(0, Math.min(rowCount - 1, yToTableIndex(pt.py)));
    const dayStart = range.startHour * 60;
    const dayEnd = range.endHour * 60;

    let { startMin, endMin, tableIndices } = drag;

    if (drag.mode === 'move') {
      const dur = drag.origEndMin - drag.origStartMin;
      let s = snap(clampMinutes(pointerMin - drag.grabOffsetMin, range));
      s = Math.max(dayStart, Math.min(s, dayEnd - dur));
      startMin = s;
      endMin = s + dur;
      const count = drag.origIndices.length;
      const top = Math.max(0, Math.min(pointerRow - drag.grabOffsetRow, rowCount - count));
      tableIndices = Array.from({ length: count }, (_, i) => top + i);
    } else if (drag.mode === 'end') {
      endMin = Math.min(Math.max(snap(pointerMin), drag.origStartMin + MIN_DURATION_MINUTES), dayEnd);
      startMin = drag.origStartMin;
      tableIndices = drag.origIndices;
    } else if (drag.mode === 'start') {
      startMin = Math.max(Math.min(snap(pointerMin), drag.origEndMin - MIN_DURATION_MINUTES), dayStart);
      endMin = drag.origEndMin;
      tableIndices = drag.origIndices;
    } else if (drag.mode === 'top') {
      const bottom = drag.origIndices[drag.origIndices.length - 1];
      const top = Math.max(0, Math.min(pointerRow, bottom));
      tableIndices = Array.from({ length: bottom - top + 1 }, (_, i) => top + i);
    } else if (drag.mode === 'bottom') {
      const top = drag.origIndices[0];
      const bottom = Math.max(top, Math.min(pointerRow, rowCount - 1));
      tableIndices = Array.from({ length: bottom - top + 1 }, (_, i) => top + i);
    }

    setDrag({ ...drag, startMin, endMin, tableIndices });
  };

  const handleBarPointerUp = () => {
    if (!drag || !range) return;
    const d = drag;
    setDrag(null);

    const changed =
      d.startMin !== d.origStartMin ||
      d.endMin !== d.origEndMin ||
      d.tableIndices.join(',') !== d.origIndices.join(',');

    if (!changed) {
      activeRef.current = false;
      return;
    }
    suppressClickRef.current = true;

    // Build new table id list, preserving any tables on other (hidden) floors.
    const floorPosIds = new Set(visibleTables.map((t) => t.posTableId));
    const preserved = csvToIds(d.booking.shopTableIds).filter((id) => !floorPosIds.has(id));
    const newPosIds = [...preserved, ...d.tableIndices.map((i) => visibleTables[i].posTableId)];

    const startIso = minutesOfDayToIso(d.startMin, selectedDate);
    const endIso = minutesOfDayToIso(d.endMin, selectedDate);

    // Block all overlaps on any target table.
    for (const posId of newPosIds) {
      const others = bookingsByPosId.get(posId) ?? [];
      for (const o of others) {
        if (o.uuid === d.booking.uuid) continue;
        if (instantsOverlap(startIso, endIso, o.startTime, o.endTime)) {
          setNotice(`Conflicts with an existing booking on ${nameByPosId.get(posId) ?? 'a table'}.`);
          activeRef.current = false;
          return; // bar snaps back (no override kept)
        }
      }
    }

    // Keep the dragged geometry visible until the server round-trip settles.
    setPending({ key: d.key, startMin: d.startMin, endMin: d.endMin, tableIndices: d.tableIndices });

    const payload: BookingMutationPayload = {
      startTime: startIso,
      endTime: endIso,
      partySize: d.booking.partySize,
      customerName: d.booking.customerName,
      customerPhone: d.booking.customerPhone,
      customerEmail: d.booking.customerEmail,
      shopTableIds: newPosIds.join(','),
      shopTableNames: newPosIds.map((id) => nameByPosId.get(id) ?? `Table ${id}`).join(','),
      bookingSection: d.booking.bookingSection,
      status: (d.booking.status as SyncedBookingStatus | null) ?? null,
      notes: d.booking.notes,
      specialRequests: d.booking.specialRequests,
      depositAmount: d.booking.depositAmount,
      depositPaid: d.booking.depositPaid,
    };
    mutation.mutate({ uuid: d.booking.uuid!, payload });
  };

  const handleCanvasClick = (e: ReactPointerEvent | React.MouseEvent) => {
    if (!canCreate || !range || dayClosed) return;
    const el = canvasRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const px = e.clientX - rect.left;
    const py = e.clientY - rect.top;
    const min = clampMinutes(snap(xToMinutes(px, range)), range);
    const idx = Math.max(0, Math.min(visibleTables.length - 1, yToTableIndex(py)));
    const table = visibleTables[idx];
    if (!table) return;
    const startIso = minutesOfDayToIso(min, selectedDate);
    // Disallow creating bookings in the past.
    if (new Date(startIso).getTime() < Date.now()) {
      setNotice('Cannot create a booking in the past.');
      return;
    }
    // Only allow creating inside a section band; preselect that section.
    const section = sectionAt(min);
    if (!section) return;
    const endMin = Math.min(min + DEFAULT_DURATION_MINUTES, range.endHour * 60);
    onCreateBooking?.({
      startTime: startIso,
      endTime: minutesOfDayToIso(endMin, selectedDate),
      tableIds: [table.posTableId],
      section,
    });
  };

  // ---- Render ---------------------------------------------------------------
  if (isLoading) {
    return (
      <div className="p-8 text-center">
        <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600" />
        <p className="mt-2 text-sm text-gray-500">Loading timeline...</p>
      </div>
    );
  }

  if (tables.length === 0) {
    return <div className="p-8 text-center text-gray-500">No tables synced yet.</div>;
  }

  if (dayClosed) {
    return (
      <div className="p-10 text-center">
        <p className="text-lg font-semibold text-gray-700">Closed for bookings on this day</p>
        <p className="mt-1 text-sm text-gray-500">This weekday has no booking sections configured.</p>
      </div>
    );
  }

  const rowCount = visibleTables.length;
  const width = rangeWidthPx(range);

  return (
    <div className="p-4 space-y-3">
      {/* Toolbar: floor selector + legend */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <label htmlFor="timeline-floor" className="text-sm font-medium text-gray-700">
            Floor
          </label>
          <select
            id="timeline-floor"
            value={effectiveFloorId ?? ''}
            onChange={(e) => setFloorId(e.target.value ? Number(e.target.value) : null)}
            className="rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
          >
            {floors.map((f) => (
              <option key={f.id} value={f.id}>
                {f.name}
              </option>
            ))}
          </select>
        </div>

        <div className="flex items-center gap-3 text-xs text-gray-600">
          {[
            ['Pending', 'bg-amber-300'],
            ['Confirmed', 'bg-green-300'],
            ['Seated', 'bg-blue-300'],
            ['Completed', 'bg-gray-300'],
          ].map(([label, cls]) => (
            <span key={label} className="inline-flex items-center gap-1">
              <span className={`inline-block h-3 w-3 rounded ${cls}`} />
              {label}
            </span>
          ))}
        </div>
      </div>

      {notice && (
        <div className="rounded-md bg-red-50 border border-red-200 px-3 py-2 text-sm text-red-700">
          {notice}
        </div>
      )}

      <div ref={scrollRef} className="overflow-auto border border-gray-200 rounded-lg" style={{ maxHeight: 600 }}>
          <div style={{ width: TABLE_LABEL_WIDTH + width }}>
            {/* Header row */}
            <div className="flex sticky top-0 z-30">
              <div
                className="sticky left-0 z-40 bg-gray-100 border-b border-r border-gray-200 flex items-center px-2 text-sm font-semibold text-gray-700"
                style={{ width: TABLE_LABEL_WIDTH, height: HEADER_HEIGHT }}
              >
                Tables
              </div>
              <div
                className="relative bg-gray-100 border-b border-gray-200"
                style={{ width, height: HEADER_HEIGHT }}
              >
                {/* Section colour blocks */}
                {sectionBlocks.map((blk, i) =>
                  blk.color ? (
                    <div
                      key={`sec-${i}`}
                      className="absolute top-0 bottom-0 opacity-30"
                      style={{ left: blk.x, width: blk.width, backgroundColor: blk.color }}
                      title={blk.name}
                    />
                  ) : null,
                )}
                {/* Hour labels */}
                {Array.from({ length: range.endHour - range.startHour }, (_, i) => (
                  <div
                    key={`hl-${i}`}
                    className="absolute top-0 bottom-0 flex items-center justify-center text-xs font-medium text-gray-700 border-l border-gray-300"
                    style={{ left: i * HOUR_WIDTH, width: HOUR_WIDTH }}
                  >
                    {String((range.startHour + i) % 24).padStart(2, '0')}:00
                  </div>
                ))}
              </div>
            </div>

            {/* Body row */}
            <div className="flex">
              {/* Table label column (sticky left) */}
              <div className="sticky left-0 z-20 bg-white" style={{ width: TABLE_LABEL_WIDTH }}>
                {visibleTables.map((t, i) => (
                  <div
                    key={t.id}
                    className={`border-b border-r border-gray-200 px-2 flex flex-col justify-center ${
                      i % 2 === 1 ? 'bg-gray-50' : 'bg-white'
                    }`}
                    style={{ height: ROW_HEIGHT }}
                  >
                    <span className="text-sm font-medium text-gray-800 truncate">{tableName(t)}</span>
                    {t.capacity != null && (
                      <span className="text-xs text-gray-500">Cap: {t.capacity}</span>
                    )}
                  </div>
                ))}
              </div>

              {/* Canvas */}
              <div
                ref={canvasRef}
                data-testid="timeline-canvas"
                className="relative"
                style={{ width, height: rowCount * ROW_HEIGHT }}
                onClick={handleCanvasClick}
              >
                <TimelineGrid range={range} rowCount={rowCount} />

                {nowX != null && nowX > 0 && (
                  <div
                    className="absolute top-0 left-0 z-10 bg-gray-400/20 pointer-events-none"
                    style={{ width: nowX, height: rowCount * ROW_HEIGHT }}
                    title="Past — new bookings not allowed"
                  />
                )}

                {nowX != null && (
                  <div
                    className="absolute top-0 z-30 w-0.5 bg-red-500 pointer-events-none"
                    style={{ left: nowX, height: rowCount * ROW_HEIGHT }}
                  />
                )}

                {renderedBars.map((bar) => (
                  <TimelineBar
                    key={bar.key}
                    bar={bar}
                    selected={!!bar.booking.uuid && bar.booking.uuid === selectedBookingUuid}
                    active={drag?.key === bar.key || pending?.key === bar.key}
                    onPointerDown={(e) => handleBarPointerDown(e, bar)}
                    onPointerMove={handleBarPointerMove}
                    onPointerUp={handleBarPointerUp}
                    onClick={(e) => {
                      e.stopPropagation();
                      if (suppressClickRef.current) {
                        suppressClickRef.current = false;
                        return;
                      }
                      if (canUpdate) onEditBooking?.(bar.booking);
                    }}
                  />
                ))}
              </div>
            </div>
          </div>
        </div>
    </div>
  );
}
